import { motion } from 'motion/react'
import { VegaLite } from 'react-vega'
import { BarChartSkeleton } from './BarChartSkeleton'
import { useCallback, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { useIsDarkMode } from '../Triangle/useIsDarkMode'
import { getBoundParamCount, getChartPlotHeight, getEstimatedBindingsOffset } from './chartSpecUtils'

interface RenderChartProps {
  hasHeader: boolean
  specWithoutConfig: object
  config?: object
  tileContainerRef: React.RefObject<HTMLDivElement>
}

const THEME_AXIS_COLORS = {
  labelColor: 'hsl(var(--muted-foreground))',
  titleColor: 'hsl(var(--foreground))',
  tickColor: 'hsl(var(--border))',
  domainColor: 'hsl(var(--border))',
  gridColor: 'hsl(var(--border))',
}

function withThemeAxisColors(axisConfig: Record<string, unknown> = {}) {
  return {
    ...axisConfig,
    ...THEME_AXIS_COLORS,
  }
}

function mergeChartRenderConfig(config: object = {}) {
  const base = config as Record<string, Record<string, unknown> | unknown>
  const view = (base.view ?? {}) as Record<string, unknown>
  const legend = (base.legend ?? {}) as Record<string, unknown>
  const style = (base.style ?? {}) as Record<string, Record<string, unknown>>

  return {
    ...base,
    background: 'transparent',
    axis: withThemeAxisColors(base.axis as Record<string, unknown> | undefined),
    axisX: withThemeAxisColors(base.axisX as Record<string, unknown> | undefined),
    axisY: withThemeAxisColors(base.axisY as Record<string, unknown> | undefined),
    axisBand: withThemeAxisColors(base.axisBand as Record<string, unknown> | undefined),
    legend: {
      ...legend,
      labelColor: 'hsl(var(--foreground))',
      titleColor: 'hsl(var(--foreground))',
    },
    style: {
      ...style,
      'guide-label': {
        ...(style['guide-label'] ?? {}),
        fill: 'hsl(var(--muted-foreground))',
      },
      'guide-title': {
        ...(style['guide-title'] ?? {}),
        fill: 'hsl(var(--foreground))',
      },
    },
    view: {
      ...view,
      fill: view.fill ?? 'transparent',
    },
  }
}

export const RenderChart = ({ specWithoutConfig, config = {}, tileContainerRef }: RenderChartProps) => {
  const chartContainerRef = useRef<HTMLDivElement>(null)
  const chartAreaRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ width: -1, height: -1 })
  const [bindingsOffset, setBindingsOffset] = useState<number | null>(null)
  const isDarkMode = useIsDarkMode()
  const chartConfig = mergeChartRenderConfig(config)
  const boundParamCount = getBoundParamCount(specWithoutConfig)
  const hasBoundParams = boundParamCount > 0

  useEffect(() => {
    if (!hasBoundParams) {
      setBindingsOffset(null)
    }
  }, [hasBoundParams])

  useEffect(() => {
    const element = chartContainerRef.current
    if (!element) return

    const updateSize = () => {
      const measuredElement = chartContainerRef.current
      const fallback = tileContainerRef.current
      const width = measuredElement?.clientWidth ?? fallback?.clientWidth ?? -1
      const height = measuredElement?.clientHeight ?? fallback?.clientHeight ?? -1

      if (width > 0 && height > 0) {
        setSize({ width: width - 100, height })
      }
    }

    const timeoutId = window.setTimeout(updateSize, 600)

    const observer = new ResizeObserver(updateSize)
    observer.observe(element)

    return () => {
      window.clearTimeout(timeoutId)
      observer.disconnect()
    }
  }, [tileContainerRef])

  const { width, height } = size
  const resolvedBindingsOffset = bindingsOffset ?? getEstimatedBindingsOffset(specWithoutConfig)
  const plotHeight = getChartPlotHeight(height, resolvedBindingsOffset)

  const measureBindingsOffset = useCallback(() => {
    const chartArea = chartAreaRef.current
    if (!chartArea || !hasBoundParams) return

    const embed = chartArea.querySelector('.vega-embed')
    const svg = embed?.querySelector('svg')
    if (!embed || !svg) return

    const offset = Math.ceil(svg.getBoundingClientRect().top - embed.getBoundingClientRect().top)
    if (offset > 0) {
      setBindingsOffset((current) => (current === offset ? current : offset))
    }
  }, [hasBoundParams])

  useEffect(() => {
    if (!hasBoundParams || width <= 0 || height <= 0) return

    const chartArea = chartAreaRef.current
    if (!chartArea) return

    const timeoutId = window.setTimeout(measureBindingsOffset, 0)
    const observer = new ResizeObserver(measureBindingsOffset)
    observer.observe(chartArea)

    return () => {
      window.clearTimeout(timeoutId)
      observer.disconnect()
    }
  }, [hasBoundParams, height, isDarkMode, measureBindingsOffset, plotHeight, specWithoutConfig, width])

  return (
    <div
      ref={chartContainerRef}
      className={clsx('box-border h-full overflow-hidden rounded-md border p-px transition-all', {
        'flex items-center justify-center border-none bg-offwhite': width < 1 || height < 1,
      })}
    >
      {width > 0 && height > 0 ? (
        <motion.div
          ref={chartAreaRef}
          className={clsx(
            'w-full [&_.vega-bindings]:mb-2 [&_.vega-bindings]:pl-5 [&_.vega-bindings]:text-foreground [&_.vega-bindings_label]:text-foreground [&_.vega-bindings_select]:ml-5 [&_.vega-bindings_select]:rounded-md [&_.vega-bindings_select]:border [&_.vega-bindings_select]:p-0.5 [&_.vega-bindings_select]:text-foreground [&_g.role-axis-label_text]:!fill-[hsl(var(--muted-foreground))] [&_g.role-axis-tick]:!stroke-[hsl(var(--border))] [&_g.role-axis-title>rect]:!fill-transparent [&_g.role-axis-title_text]:!fill-[hsl(var(--foreground))]',
            hasBoundParams ? 'min-h-0' : 'h-full'
          )}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <VegaLite
            key={isDarkMode ? 'dark' : 'light'}
            onNewView={() => {
              window.requestAnimationFrame(measureBindingsOffset)
            }}
            spec={{
              ...specWithoutConfig,
              width,
              height: plotHeight,
              autosize: {
                type: 'fit',
                contains: 'padding',
              },
            }}
            config={chartConfig}
            actions={true}
            {...({ ast: true } as Record<string, unknown>)}
          />
        </motion.div>
      ) : (
        <BarChartSkeleton />
      )}
    </div>
  )
}
